import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
  useSecondaryColor,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnOnLight } from '../utils/buttonStyles';
import { relativeLuminance } from '../utils/luminance';
import { PhotoLightbox } from './shared/PhotoLightbox';
import { ctaLinkProps } from '../utils/scheduleTourModal';

const availabilityCtaDef: SlotGroupDefinition = {
  label: 'See Availability CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'SEE AVAILABILITY' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface GalleryImage {
  id?: string | null;
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  video_url?: string | null;
}

type GalleryTab = 'all' | 'apartments' | 'community' | 'videos' | 'modern';

// photo_gallery has no field that separates the renovated units from the
// original apartment photos, so the Modern New Apartments tab keys off item ids.
const MODERN_APARTMENT_PHOTO_IDS = new Set([
  'custom_2080f123-ce5f-41ea-83a4-052770350fcc',
  'custom_3e7f7286-91cb-4719-8a55-c86472abf05f',
  'custom_8f7e1716-9757-4c7a-84e4-1f8023ca77f5',
  'custom_4ad99ef0-9cc8-427f-96bc-491a4eeed2b8',
  'custom_6e8a67f4-d8b3-4ea1-b1e6-4aa9f54a9f1d',
  'custom_1df56639-bc88-44b2-96d9-700bd9cf855e',
  'custom_4cb0e04b-3902-4869-9239-8c3e0277e913',
  'custom_8055c550-2a6f-4659-a31f-c2f799370af4',
  'custom_87883887-89d7-413a-81b5-5009ec37418b',
]);

function resolveSrc(photo: GalleryImage): string {
  return photo.image || photo.original_url || '';
}

function normalizeTitle(title: string | null | undefined): string {
  return (title || '').trim().toLowerCase();
}

function isModernPhoto(photo: GalleryImage): boolean {
  return Boolean(photo.id && MODERN_APARTMENT_PHOTO_IDS.has(photo.id));
}

function isApartmentPhoto(photo: GalleryImage): boolean {
  if (isModernPhoto(photo)) return false;
  const t = normalizeTitle(photo.title);
  return t === 'apartment' || t === 'apartments' || t.includes('apartment');
}

function isCommunityPhoto(photo: GalleryImage): boolean {
  const t = normalizeTitle(photo.title);
  return t === 'community' || t.includes('community') || t.includes('amenit');
}

/**
 * Unified photo gallery matching the 505 Sutton layout: All / Apartments /
 * Community filter tabs above a centered 3-column image grid with lightbox.
 * Pulls every photo_gallery item (apartment + community) into one section.
 */
export function Gallery() {
  const availabilityCta = useSlotGroup('gallery.availability_cta', availabilityCtaDef);
  const secondary = useSecondaryColor();
  // Near-white secondary blends into the page — outline hover/active tabs in black.
  const secondaryNeedsBorder = relativeLuminance(secondary) > 0.85;

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const videoUrl = useContentToken('gallery.video');
  const videoPoster = useContentToken('gallery.video_poster');
  const videoTitle = useContentToken('gallery.video_title');

  const [activeTab, setActiveTab] = useState<GalleryTab>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const usable = useMemo(
    () => images.filter((photo) => Boolean(resolveSrc(photo))),
    [images],
  );

  const videos = useMemo<GalleryImage[]>(() => {
    const url = videoUrl?.trim();
    if (!url) return [];
    return [
      {
        id: 'gallery-video',
        image: videoPoster?.trim() || '',
        title: videoTitle?.trim() || 'Property video',
        video_url: url,
      },
    ];
  }, [videoUrl, videoPoster, videoTitle]);

  const filtered = useMemo(() => {
    if (activeTab === 'apartments') return usable.filter(isApartmentPhoto);
    if (activeTab === 'community') return usable.filter(isCommunityPhoto);
    if (activeTab === 'videos') return videos;
    if (activeTab === 'modern') return usable.filter(isModernPhoto);
    // All: apartments, community, videos, modern new apartments, then the rest.
    const apartments = usable.filter(isApartmentPhoto);
    const community = usable.filter(
      (photo) => !isApartmentPhoto(photo) && isCommunityPhoto(photo),
    );
    const modern = usable.filter(isModernPhoto);
    const rest = usable.filter(
      (photo) => !isApartmentPhoto(photo) && !isCommunityPhoto(photo) && !isModernPhoto(photo),
    );
    return [...apartments, ...community, ...videos, ...modern, ...rest];
  }, [usable, videos, activeTab]);

  // Reset lightbox selection when the visible set changes.
  useEffect(() => {
    setSelectedIndex(null);
  }, [activeTab]);

  return (
    <section className="bg-white">
      <div className="photo-gallery">
        <div className="photo-gallery__intro">
          <RichTextContent
            path="gallery.blurb"
            as="div"
            className="photo-gallery__blurb font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto leading-relaxed [&_a]:text-primary [&_a]:underline"
          />
          <EditableSlotGroup
            groupPath="gallery.availability_cta"
            definition={availabilityCtaDef}
          >
            <a
              {...ctaLinkProps(availabilityCta.url || '/floor-plans', availabilityCta._target)}
              className={`inline-block text-center mt-6 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase ${btnOnLight}`}
            >
              {availabilityCta.label || 'SEE AVAILABILITY'}
            </a>
          </EditableSlotGroup>
        </div>

        <div className="photo-gallery__tabs" role="tablist" aria-label="Gallery categories">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all'}
            className={`photo-gallery__tab${activeTab === 'all' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <RichTextContent path="gallery.tab_all" as="span" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'apartments'}
            className={`photo-gallery__tab${activeTab === 'apartments' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('apartments')}
          >
            <RichTextContent path="gallery.tab_apartments" as="span" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'community'}
            className={`photo-gallery__tab${activeTab === 'community' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('community')}
          >
            <RichTextContent path="gallery.tab_community" as="span" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'videos'}
            className={`photo-gallery__tab${activeTab === 'videos' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('videos')}
          >
            <RichTextContent path="gallery.tab_videos" as="span" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'modern'}
            className={`photo-gallery__tab${activeTab === 'modern' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('modern')}
          >
            <RichTextContent path="gallery.tab_modern" as="span" />
          </button>
        </div>

        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Images">
          {filtered.length === 0 ? (
            <div className="photo-gallery__empty">
              <p className="font-body text-lg text-[var(--vibe-color-text-muted)]">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <ul className="photo-gallery__grid">
              {filtered.map((photo, index) => {
                const src = resolveSrc(photo);
                const alt = photo.alt_text || photo.title || 'Property photo';
                if (photo.video_url) {
                  return (
                    <li key={`video-${photo.video_url}`} className="photo-gallery__item">
                      <EditableToken
                        path="gallery.video"
                        type="video"
                        label="Gallery Video"
                        as="div"
                        className="photo-gallery__editable"
                      >
                        <EditableToken
                          path="gallery.video_poster"
                          type="image"
                          label="Video Thumbnail"
                          as="div"
                          className="photo-gallery__editable"
                        >
                          <EditableToken
                            path="gallery.video_title"
                            type="text"
                            label="Video Title"
                            as="div"
                            className="photo-gallery__editable"
                          >
                            <button
                              type="button"
                              className="photo-gallery__thumb photo-gallery__thumb--video"
                              onClick={() => setSelectedIndex(index)}
                              aria-label={`Play ${photo.title || 'video'}`}
                            >
                              {src ? (
                                <VibesitesImage
                                  url={src}
                                  alt=""
                                  sizes="(min-width: 960px) 33vw, (min-width: 768px) 48vw, 100vw"
                                  className="photo-gallery__img"
                                />
                              ) : null}
                              <span className="photo-gallery__play" aria-hidden="true">
                                <svg viewBox="0 0 512 512" width="56" height="56" fill="currentColor">
                                  <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 464c-118.7 0-216-96.1-216-216 0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216 0 118.7-96.1 216-216 216zm-40-306.5v181c0 9.2 10 15 18 10.4l157-90.5c8-4.6 8-16.1 0-20.7l-157-90.5c-8-4.7-18 1.1-18 10.3z" />
                                </svg>
                              </span>
                            </button>
                          </EditableToken>
                        </EditableToken>
                      </EditableToken>
                    </li>
                  );
                }
                return (
                  <li key={`${src}-${index}`} className="photo-gallery__item">
                    <button
                      type="button"
                      className="photo-gallery__thumb"
                      onClick={() => setSelectedIndex(index)}
                      aria-label={`View ${alt}`}
                    >
                      <VibesitesImage
                        url={src}
                        alt={alt}
                        sizes="(min-width: 960px) 33vw, (min-width: 768px) 48vw, 100vw"
                        className="photo-gallery__img"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </EditableCollectionBinding>
      </div>

      <PhotoLightbox
        images={filtered}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <style>{`
        .photo-gallery {
          width: 100%;
          max-width: 1300px;
          margin: 0 auto;
          padding: 2.5rem 1rem 3.5rem;
          text-align: center;
        }

        .photo-gallery__intro {
          margin: 0 auto 2rem;
          max-width: 48rem;
        }

        .photo-gallery__blurb p {
          margin: 0 0 0.75em;
        }

        .photo-gallery__blurb p:last-child {
          margin-bottom: 0;
        }

        .photo-gallery__tabs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 1.25rem;
        }

        .photo-gallery__tab {
          appearance: none;
          border: 1px solid transparent;
          border-radius: 0;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          line-height: 1;
          padding: 0.95rem 1.85rem;
          cursor: pointer;
          text-decoration: none;
          transition: background-color 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;
        }

        .photo-gallery__tab:hover {
          opacity: 0.92;
        }

        .photo-gallery__tab.is-active {
          background: var(--vibe-color-secondary, #2e3246);
          color: var(--vibe-color-text-on-secondary, #fff);
        }

        .photo-gallery__tab.needs-contrast-border:hover,
        .photo-gallery__tab.needs-contrast-border.is-active {
          border-color: #000;
        }

        .photo-gallery__empty {
          padding: 3rem 1rem;
        }

        .photo-gallery__grid {
          list-style: none;
          margin: 0;
          padding: 0;
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        }

        .photo-gallery__item {
          flex: 0 0 33.333%;
          max-width: 33.333%;
          height: 290px;
          max-height: 290px;
          margin: 0;
          padding: 5px;
          position: relative;
          overflow: hidden;
          animation: photo-gallery-fade-in 0.35s ease both;
        }

        .photo-gallery__thumb {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 1px solid #555;
          background: transparent;
          cursor: pointer;
          overflow: hidden;
        }

        .photo-gallery__editable {
          display: block;
          width: 100%;
          height: 100%;
        }

        .photo-gallery__thumb--video {
          position: relative;
          background: #000;
        }

        .photo-gallery__play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background: rgba(0, 0, 0, 0.2);
          pointer-events: none;
        }

        .photo-gallery__img {
          display: block;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
          transition: transform 0.45s ease;
        }

        .photo-gallery__thumb:hover .photo-gallery__img,
        .photo-gallery__thumb:focus-visible .photo-gallery__img {
          transform: scale(1.06);
        }

        @keyframes photo-gallery-fade-in {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .photo-gallery__item { animation: none; }
          .photo-gallery__img { transition: none; }
        }

        /* Tablet: 2 columns */
        @media (max-width: 959px) and (min-width: 768px) {
          .photo-gallery__item {
            flex: 0 0 48%;
            max-width: 48%;
            height: 220px;
            max-height: 220px;
            margin: 5px;
            padding: 0;
          }
        }

        /* Mobile: single column */
        @media (max-width: 767px) {
          .photo-gallery {
            padding: 1.75rem 0.75rem 2.5rem;
          }

          .photo-gallery__tabs {
            flex-direction: column;
            align-items: stretch;
            gap: 0.35rem;
          }

          .photo-gallery__tab {
            width: 100%;
            margin: 0;
          }

          .photo-gallery__item {
            flex: 0 0 100%;
            max-width: 100%;
            height: 220px;
            max-height: 220px;
            margin: 5px 0;
            padding: 0;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {
    'gallery.blurb': {
      label: 'Gallery Blurb',
      type: 'richtext',
      defaultField: 'property_gallery_blurb',
      defaultValue: '',
    },
    'gallery.tab_all': {
      label: 'All Tab Label',
      type: 'richtext',
      defaultValue: '<p>All</p>',
    },
    'gallery.tab_apartments': {
      label: 'Apartments Tab Label',
      type: 'richtext',
      defaultValue: '<p>Apartments</p>',
    },
    'gallery.tab_community': {
      label: 'Community Tab Label',
      type: 'richtext',
      defaultValue: '<p>Community</p>',
    },
    'gallery.tab_videos': {
      label: 'Videos Tab Label',
      type: 'richtext',
      defaultValue: '<p>Videos</p>',
    },
    'gallery.tab_modern': {
      label: 'Modern New Apartments Tab Label',
      type: 'richtext',
      defaultValue: '<p>Modern New Apartments</p>',
    },
    'gallery.video': {
      label: 'Gallery Video',
      type: 'video',
      defaultValue:
        'https://vid.cdn-website.com/699eef81/videos/3z5b21mZRrWJhhKMMxgC_Woodview+at+Randolph+Randolph+MA+Apartments+The+Dolben+Company-v.mp4',
      description: 'Shown in the Videos tab and the All tab. Leave empty to hide the video tile.',
    },
    'gallery.video_poster': {
      label: 'Video Thumbnail',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/699eef81/dms3rep/multi/opt/Screenshot+2023-11-10+at+4.18.53+PM-1920w.png',
    },
    'gallery.video_title': {
      label: 'Video Title',
      type: 'text',
      defaultValue: 'Woodview at Randolph Apartments Video',
      description: 'Accessible title for the video player.',
    },
  },
  slotGroups: {
    'gallery.availability_cta': availabilityCtaDef,
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
      description:
        'All property photos in one grid. Use photo titles of "Apartment" / "Community" so the Apartments and Community tabs can filter the set. The renovated-unit photos appear under Modern New Apartments instead of Apartments. The All tab shows every photo plus the video.',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
